import { Button, Checkbox, DatePicker, Form, Input, InputNumber, Select, Space, Table, Tag, message } from 'antd';
import { SorterResult } from 'antd/es/table/interface';
import { getInternalOrderActions } from 'api/coreapi/orders';
import { InternalOrderTaskTypes } from 'constants/orders';
import { InternalOrderActionsUpdatedEvent } from 'constants/events';
import dayjs from 'dayjs';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PATHS from 'routes/paths';
import { InternalOrderActionQueueItem } from 'shared/models/internalOrderActionModel';
import { GetInternalOrderActionsPayload } from 'shared/models/payloads/getInternalOrderActionsPayload';
import RetrySupplierConfirmationModal from './retrySupplierConfirmationModal';

const { RangePicker } = DatePicker;

const retryableTypes = [
    InternalOrderTaskTypes.ResolveNoEligibleSuppliers,
    InternalOrderTaskTypes.ResolveNoConfirmedQuotes,
];

const InternalActionsQueue = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [actions, setActions] = useState<InternalOrderActionQueueItem[]>([]);
    const [pagination, setPagination] = useState({ current: 1, pageSize: 15, total: 0 });
    const currentPage = pagination.current;
    const pageSize = pagination.pageSize;
    const [filters, setFilters] = useState<GetInternalOrderActionsPayload>({
        typeIds: [],
        pageNumber: 1,
        pageSize: 15,
        orderByColumns: ['deadline'],
        orderByDirection: 0,
    });

    const fetchActions = useCallback(async () => {
        setLoading(true);
        try {
            const response = await getInternalOrderActions({
                ...filters,
                pageNumber: currentPage,
                pageSize,
            });
            setActions(response.result.data);
            setPagination(current => ({ ...current, total: response.result.totalCount }));
        } catch (error: any) {
            message.error(error?.errors?.[0] ?? 'No fue posible obtener las acciones pendientes.');
        } finally {
            setLoading(false);
        }
    }, [currentPage, filters, pageSize]);

    useEffect(() => {
        fetchActions();
    }, [fetchActions]);

    useEffect(() => {
        const refreshActions = () => fetchActions();
        window.addEventListener(InternalOrderActionsUpdatedEvent, refreshActions);
        return () => window.removeEventListener(InternalOrderActionsUpdatedEvent, refreshActions);
    }, [fetchActions]);

    const applyFilters = (values: any) => {
        setFilters(current => ({
            ...current,
            typeIds: values.typeIds ?? [],
            orderNumber: values.orderNumber,
            customer: values.customer,
            isOverdue: values.isOverdue ? true : undefined,
            deadlineFrom: values.deadline?.[0]?.toISOString(),
            deadlineTo: values.deadline?.[1]?.toISOString(),
        }));
        setPagination(current => ({ ...current, current: 1 }));
    };

    const columns = [
        {
            title: 'Acción',
            dataIndex: 'typeName',
            key: 'typeName',
        },
        {
            title: '# Orden',
            dataIndex: 'orderNumber',
            key: 'orderNumber',
            render: (orderNumber: number) =>
                <Button type="link" onClick={() => navigate(PATHS.ORDER_DETAIL.replace(':orderNumber', orderNumber.toString()))}>
                    {orderNumber}
                </Button>,
        },
        {
            title: 'Cliente',
            dataIndex: 'customerName',
            key: 'customerName',
            render: (_: string, action: InternalOrderActionQueueItem) =>
                <Space direction="vertical" size={0}>
                    <span>{action.customerName}</span>
                    <span>{action.customerIdentificationNumber}</span>
                </Space>,
        },
        {
            title: 'Estado',
            dataIndex: 'orderStatusName',
            key: 'orderStatusName',
        },
        {
            title: 'Vencimiento',
            dataIndex: 'deadline',
            key: 'deadline',
            sorter: true,
            render: (deadline?: Date | null) => {
                if (!deadline) {
                    return 'Sin vencimiento';
                }
                const overdue = dayjs(deadline).isBefore(dayjs());
                return <Tag color={overdue ? 'red' : 'blue'}>
                    {dayjs(deadline).format('DD-MM-YYYY hh:mm A')}{overdue ? ' · Vencida' : ''}
                </Tag>;
            },
        },
        {
            title: 'Antigüedad',
            dataIndex: 'createdOn',
            key: 'createdOn',
            sorter: true,
            render: (createdOn: Date) => `${Math.max(0, dayjs().diff(dayjs(createdOn), 'day'))} días`,
        },
        {
            title: 'Resolver',
            key: 'resolve',
            render: (_: unknown, action: InternalOrderActionQueueItem) =>
                <Space wrap>
                    {retryableTypes.includes(action.typeId) &&
                        <RetrySupplierConfirmationModal orderNumber={action.orderNumber} onRetried={fetchActions} />}
                    <Button onClick={() => navigate(PATHS.ORDER_DETAIL.replace(':orderNumber', action.orderNumber.toString()))}>
                        Ver / cancelar
                    </Button>
                </Space>,
        },
    ];

    const onTableChange = (
        nextPagination: any,
        _: any,
        sorter: SorterResult<InternalOrderActionQueueItem> | SorterResult<InternalOrderActionQueueItem>[]
    ) => {
        const selectedSorter = Array.isArray(sorter) ? sorter[0] : sorter;
        setPagination(current => ({
            ...current,
            current: nextPagination.current,
            pageSize: nextPagination.pageSize,
        }));
        if (selectedSorter?.columnKey) {
            const orderByColumn = selectedSorter.columnKey.toString();
            setFilters(current => ({
                ...current,
                orderByColumns: [orderByColumn],
                orderByDirection: selectedSorter.order === 'descend' ? 1 : 0,
            }));
        }
    };

    return <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <Form layout="inline" onFinish={applyFilters}>
            <Form.Item name="typeIds" label="Tipo">
                <Select
                    mode="multiple"
                    allowClear
                    style={{ minWidth: 280 }}
                    options={[
                        { value: InternalOrderTaskTypes.SelectQuote, label: 'Seleccionar cotización' },
                        { value: InternalOrderTaskTypes.ResolveNoEligibleSuppliers, label: 'Sin proveedores elegibles' },
                        { value: InternalOrderTaskTypes.ResolveNoConfirmedQuotes, label: 'Sin cotizaciones confirmadas' },
                        { value: InternalOrderTaskTypes.StartService, label: 'Iniciar servicio' },
                        { value: InternalOrderTaskTypes.FinishService, label: 'Finalizar servicio' },
                    ]} />
            </Form.Item>
            <Form.Item name="deadline" label="Vencimiento">
                <RangePicker showTime />
            </Form.Item>
            <Form.Item name="orderNumber" label="# Orden">
                <InputNumber controls={false} />
            </Form.Item>
            <Form.Item name="customer" label="Cliente">
                <Input placeholder="Nombre o identificación" />
            </Form.Item>
            <Form.Item name="isOverdue" valuePropName="checked">
                <Checkbox>Solo vencidas</Checkbox>
            </Form.Item>
            <Form.Item>
                <Button type="primary" htmlType="submit">Buscar</Button>
            </Form.Item>
        </Form>

        <Table
            rowKey="id"
            dataSource={actions}
            columns={columns}
            loading={loading}
            pagination={pagination}
            onChange={onTableChange} />
    </Space>;
};

export default InternalActionsQueue;
